import React, { useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CarsTable } from '@/features/cars/components/CarsTable';
import { useCars, useCreateCar, useCar } from '@/features/cars/hooks/useCars';
import { CARS_PER_PAGE } from '@/features/cars/hooks/useCars';
import Pagination from '@/components/common/Pagination';
import PageMeta from '@/components/common/PageMeta';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import Button from '@/components/ui/button/Button';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import { useModalSearchParam } from '@/shared/hooks/useModalSearchParam';
import CarCreateForm from '@/features/cars/components/CarCreateForm';
import { CreateCarDto } from '@/features/cars/api/carsApi';
import { PlusIcon } from '@/shared/icons';
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import { ColumnFilter } from '@/components/ui/table/ColumnFilter';
import { Loader } from "@/components/ui/loader/Loader";

// Ключ для хранения настроек видимости столбцов
const CARS_TABLE_STORAGE_KEY = 'cars-table-columns';

// Столбцы, видимые по умолчанию
const DEFAULT_VISIBLE_COLUMNS = ['image', 'model', 'color', 'price', 'stats', 'actions'];

const defaultFormData: CreateCarDto = {
  model: '',
  slug: '',
  color: '',
  registration_number: '',
  release_year: new Date().getFullYear(),
  description_en: '',
  description_fr: '',
  price: '',
  ecology_class: '',
  fuel_type: 'petrol',
  mileage_limit: 0,
  drivetrain: 'front_2wd',
  fuel_consumption: 0,
  engine_displacement: 0,
  engine_power: 0,
  seats_count: 0,
  doors_count: 0,
  has_gps: false,
  clearance: 0,
  permitted_area_latitude: 0,
  permitted_area_longitude: 0,
  permitted_area_radius: 0,
};

const CarsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;

  const { data: cars, isLoading, total } = useCars({
    offset: (currentPage - 1) * CARS_PER_PAGE,
    limit: CARS_PER_PAGE,
  });

  const editCarId = searchParams.get('edit');
  const { data: editCar } = useCar(editCarId || '');

  const { isOpen: isCreateOpen, open: openCreateModal, close: closeCreateModalWithoutCheck } = useModalSearchParam({
    paramName: 'create'
  });

  const createCar = useCreateCar();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);

  const handleModalClose = useCallback(() => {
    formRef.current?.handleClose();
  }, []);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (hasUnsavedChanges === false) {
      closeCreateModalWithoutCheck();
    }
  }, [closeCreateModalWithoutCheck]);

  const handleCreateCar = async (data: CreateCarDto) => {
    try {
      await createCar.mutateAsync(data);
      closeCreateModalWithoutCheck();
    } catch (error) {
      throw error;
    }
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  // Формируем массив столбцов с переводами
  const CAR_COLUMNS = [
    { key: 'image', header: t('cars.columns.image'), field: 'image' },
    { key: 'model', header: t('cars.columns.info'), field: 'model' },
    { key: 'color', header: t('cars.columns.color'), field: 'color' },
    { key: 'price', header: t('cars.columns.price'), field: 'price' },
    { key: 'stats', header: t('cars.columns.usage_stats'), field: 'stats' },
    { key: 'actions', header: t('cars.columns.actions'), field: 'actions' },
  ];

  return (
    <>
      <PageMeta
        title={t('cars.title')}
        description={t('cars.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.cars') }
        ]}
      />

      <TableColumnsFilter
        columns={CAR_COLUMNS}
        storageKey={CARS_TABLE_STORAGE_KEY}
        defaultColumns={DEFAULT_VISIBLE_COLUMNS}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <ColumnFilter
              size="sm"
              buttonText={t('filters.columns')}
              className="w-auto"
            />
          </div>
          <Button
            size="sm"
            variant="primary"
            startIcon={<PlusIcon />}
            onClick={openCreateModal}
          >
            {t('cars.add_new')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center min-h-[400px] bg-bg-secondary rounded-lg">
            <Loader />
          </div>
        ) : (
          <>
            <div className="bg-bg-secondary rounded-lg">
              <CarsTable cars={cars || []} isLoading={false} initialEditCar={editCar} />
            </div>
            <div className="mt-4 flex justify-end">
              <Pagination
                totalItems={total || 0}
                itemsPerPage={CARS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </TableColumnsFilter>

      <ModalWithSearchParam
        paramName="create"
        maxWidth="1500px"
        className="p-6 lg:p-10"
        onClose={handleModalClose}
      >
        <CarCreateForm
          ref={formRef}
          onSubmit={handleCreateCar}
          onClose={handleFormClose}
          initialData={defaultFormData}
        />
      </ModalWithSearchParam>
    </>
  );
};

export default CarsPage;
